import React from 'react';
import './DateTimePicker.css';

/**
 * DateTimePicker
 * Combines a native date input (triggers calendar) with explicit
 * hour + minute selects for reliable cross-browser time picking.
 *
 * value / onChange use ISO datetime-local strings: 'YYYY-MM-DDTHH:mm'
 */
const DateTimePicker = ({ label, value, onChange, min, error, required }) => {
  // Split the ISO string into date and time parts
  const [datePart, timePart] = value ? value.split('T') : ['', ''];
  const hour   = timePart ? timePart.slice(0, 2) : '';
  const minute = timePart ? timePart.slice(3, 5) : '';

  // Derive min date for the date input
  const minDate = min ? min.split('T')[0] : '';

  const emit = (newDate, newHour, newMinute) => {
    if (!newDate) { onChange(''); return; }
    const h = newHour   || '00';
    const m = newMinute || '00';
    onChange(`${newDate}T${h}:${m}`);
  };

  // Build hour options 00–23
  const hours = Array.from({ length: 24 }, (_, i) =>
    String(i).padStart(2, '0')
  );

  // Build minute options in 5-min steps: 00, 05, 10 … 55
  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, '0')
  );

  return (
    <div className={`dtp-field ${error ? 'dtp-field--error' : ''}`}>
      {label && (
        <label className="dtp-field__label">
          {label}{required && <span className="dtp-field__required">*</span>}
        </label>
      )}

      <div className="dtp-inputs">
        {/* ── Date input — triggers native calendar ── */}
        <input
          className="dtp-date"
          type="date"
          value={datePart}
          min={minDate}
          required={required}
          onChange={(e) => emit(e.target.value, hour, minute)}
        />

        {/* ── Time section ── */}
        <div className="dtp-time">
          <svg
            className="dtp-time__icon"
            viewBox="0 0 20 20" fill="currentColor" width="14" height="14"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clipRule="evenodd"
            />
          </svg>

          <select
            className="dtp-select"
            value={hour}
            disabled={!datePart}
            onChange={(e) => emit(datePart, e.target.value, minute)}
            aria-label="Hour"
          >
            <option value="">HH</option>
            {hours.map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>

          <span className="dtp-time__sep">:</span>

          <select
            className="dtp-select"
            value={minute}
            disabled={!datePart}
            onChange={(e) => emit(datePart, hour, e.target.value)}
            aria-label="Minute"
          >
            <option value="">MM</option>
            {minutes.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Formatted preview */}
      {datePart && hour && minute && (
        <span className="dtp-field__preview">
          {new Date(`${datePart}T${hour}:${minute}`).toLocaleString('en-NA', {
            weekday: 'short', year: 'numeric', month: 'short',
            day: 'numeric', hour: '2-digit', minute: '2-digit',
          })}
        </span>
      )}

      {error && <span className="dtp-field__error">{error}</span>}
    </div>
  );
};

export default DateTimePicker;